/**
 * Step2Company — sélection de la stratégie company (9.T.5.v3.6, QA §5).
 *
 * 3 stratégies :
 *   - attach_existing_project : le project existant fournit la company (skip)
 *   - attach_company_create_project : ExistingCompanyPicker
 *   - create_company_and_project : form new_company minimal
 */

import type { ClassificationPayload, MaterializeStrategy } from "@kollectivia/shared";
import { useTranslation } from "react-i18next";

import { ExistingCompanyPicker } from "../../../../components/ExistingCompanyPicker";

import type { WizardData } from "./MaterializeWizard";

type Props = {
  data: WizardData;
  onChange: (patch: Partial<WizardData>) => void;
  classification: ClassificationPayload | null;
};

const STRATEGIES: MaterializeStrategy[] = [
  "attach_existing_project",
  "attach_company_create_project",
  "create_company_and_project",
];

export function Step2Company({ data, onChange, classification }: Props) {
  const { t } = useTranslation();

  return (
    <section>
      <h3 className="mb-1 text-lg font-medium text-slate-900">
        {t("intake_events.detail.materialize.wizard.step_2_company.title")}
      </h3>
      <p className="mb-4 text-sm text-slate-600">
        {t("intake_events.detail.materialize.wizard.step_2_company.subtitle")}
      </p>

      <div role="radiogroup" className="mb-4 space-y-2">
        {STRATEGIES.map((s) => (
          <label
            key={s}
            className="flex cursor-pointer items-start gap-3 rounded-md border border-slate-200 p-3 hover:bg-slate-50"
          >
            <input
              type="radio"
              name="strategy"
              value={s}
              checked={data.strategy === s}
              onChange={() => onChange({ strategy: s })}
              className="focus-visible:ring-brand-500 mt-1 focus-visible:ring-2"
            />
            <span className="text-sm text-slate-800">
              {t(`intake_events.detail.materialize.strategy.${s}.label`)}
              <span className="ml-2 block text-xs text-slate-500">
                {t(`intake_events.detail.materialize.strategy.${s}.hint`)}
              </span>
            </span>
          </label>
        ))}
      </div>

      {data.strategy === "attach_existing_project" && (
        <p className="text-xs text-slate-500">
          {t(
            "intake_events.detail.materialize.wizard.step_2_company.strategy_attach_existing_project",
          )}
        </p>
      )}

      {data.strategy === "attach_company_create_project" && (
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-600">
            {t(
              "intake_events.detail.materialize.wizard.step_2_company.strategy_attach_company_create_project_label",
            )}
          </label>
          <ExistingCompanyPicker
            value={data.existingCompanyId}
            onChange={(id) => onChange({ existingCompanyId: id ?? undefined })}
          />
        </div>
      )}

      {data.strategy === "create_company_and_project" && (
        <div className="space-y-3">
          <div>
            <label
              htmlFor="new-company-legal-name"
              className="block text-xs font-medium text-slate-600"
            >
              {t("intake_events.detail.materialize.new_company.legal_name")}
            </label>
            <input
              id="new-company-legal-name"
              type="text"
              maxLength={200}
              value={data.newCompany?.legal_name ?? ""}
              onChange={(e) =>
                onChange({
                  newCompany: {
                    ...(data.newCompany ?? {}),
                    legal_name: e.target.value,
                  },
                })
              }
              className="focus-visible:border-brand-500 focus-visible:ring-brand-500 mt-1 w-full rounded border border-slate-300 p-2 text-sm focus-visible:outline-none focus-visible:ring-2"
            />
          </div>
          <div>
            <label
              htmlFor="new-company-language"
              className="block text-xs font-medium text-slate-600"
            >
              {t("intake_events.detail.materialize.new_company.language")}
            </label>
            <select
              id="new-company-language"
              value={data.newCompany?.language ?? "fr"}
              onChange={(e) =>
                onChange({
                  newCompany: {
                    ...(data.newCompany ?? {}),
                    language: e.target.value as "fr" | "en",
                  },
                })
              }
              className="focus-visible:border-brand-500 focus-visible:ring-brand-500 mt-1 w-full rounded border border-slate-300 p-2 text-sm focus-visible:outline-none focus-visible:ring-2"
            >
              <option value="fr">FR</option>
              <option value="en">EN</option>
            </select>
          </div>
        </div>
      )}
      {/* classification unused explicit reference for future pre-fill iteration */}
      {void classification}
    </section>
  );
}
